import { useCallback } from "react";
import Constants from "../../../config/Constants";
import { useAthlete } from "hooks";
import useAthleteActions from "providers/AthletesProvider/useAthleteActions";
import { useNavigate } from "react-router-dom";
import TrashIcon from "components/Icons/TrashIcon";
import { useTranslation } from "react-i18next";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import DeleteAthleteDialog from "../components/DeleteAthleteDialog";
import useBoolean from "hooks/useBoolean";
import Button from "components/Button";
import { styled } from "@mui/material";

const ButtonV2 = styled(Button)(({ theme }) => ({
  "&.MuiButton-secondary": {
    color: theme.palette.error.main,
    backgroundColor: "transparent",
    border: `2px solid ${theme.palette.error.main}`,

    "&:hover": {
      backgroundColor: theme.palette.error.light,
    },

    "&:disabled, &.Mui-disabled": {
      opacity: 0.3,
    },

    "&:focus-visible": {
      outline: `2px solid ${theme.palette.error.main}`,
    },

    "&:active": {
      backgroundColor: theme.palette.error.light,
    },
  },

  "& .MuiButton-startIcon": {
    color: theme.palette.error.main,
  },
}));

interface DeleteAthleteActionProps {
  athleteId: string;
}

function DeleteAthleteAction(props: DeleteAthleteActionProps) {
  const { athleteId } = props;

  const { t } = useTranslation();
  const { value: open, toggle } = useBoolean();
  const athleteActions = useAthleteActions();
  const navigate = useNavigate();
  const athlete = useAthlete(athleteId);

  const confirmAthleteDeletion = useCallback(async () => {
    await athleteActions.delete(athleteId);
    toggle();
    navigate(Constants.routes.athletes.route);
  }, [athleteActions, athleteId, navigate, toggle]);

  if (!athlete) return null;

  return (
    <ThemeProvider2024>
      <ButtonV2 onClick={toggle} fullWidth variant="secondary" startIcon={<TrashIcon fontSize="small" />}>
        {t("edit_athlete.delete_button_text")}
      </ButtonV2>
      <DeleteAthleteDialog open={open} onClose={toggle} onDelete={confirmAthleteDeletion} athlete={athlete} />
    </ThemeProvider2024>
  );
}

export default DeleteAthleteAction;
